import { ServiceUnavailablePage } from "@lyfegen-platform/react-common";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

enum ButtonAction {
	REFRESH_PAGE,
	RETURN_TO_HOME,
	CONTACT_SUPPORT,
	NONE
}

const errorActions = {
	"403": ButtonAction.CONTACT_SUPPORT,
	"404": ButtonAction.RETURN_TO_HOME,
	"500": ButtonAction.REFRESH_PAGE,
	"502": ButtonAction.NONE,
	"503": ButtonAction.REFRESH_PAGE,
	"504": ButtonAction.REFRESH_PAGE,
	"524": ButtonAction.REFRESH_PAGE,
}

type StatusCode = keyof typeof errorActions;

interface SpecificErrorPageProps {
	statusCode?: string;
}

/**
 * Displays a specific dialog for the error codes 403, 404, 500, 502, 503, 504, 524, or a fallback error dialog for
 * other general errors, not covered by the specified error codes.
 *
 * @param statusCode of the error to be displayed.
 */
const SpecificErrorPage: React.FC<SpecificErrorPageProps> = ({statusCode}) => {
	const { t } = useTranslation();
	const { push } = useHistory();

	const handleContactSupport = () => {
		const email = "support@lyfegen.com";
		const subject = encodeURIComponent("Error - No Permission to View");

		const mailtoLink = `mailto:${email}?subject=${subject}`;
		window.open(mailtoLink, '_blank', "noopener,noreferrer");
	};

	const refreshPage = () => {
		window.location.reload();
	}

	const handleBack = () => {
		push("/");
	};

	const getErrorProps = () => {
		const validStatusCode: StatusCode | undefined = statusCode as StatusCode;
		if (validStatusCode in errorActions) {
			return {
				statusCode: validStatusCode,
				header: t(`${validStatusCode}`),
				description: t(`${validStatusCode}-description`),
				buttonAction: errorActions[validStatusCode]
			};
		}

		return {
			statusCode: undefined,
			header: t("library-error-header"),
			description: t("library-error-header-description"),
			buttonAction: ButtonAction.REFRESH_PAGE
		};
	};

	const buttonActionMappings = {
		[ButtonAction.REFRESH_PAGE]: { text: t("refresh"), onAction: refreshPage },
		[ButtonAction.RETURN_TO_HOME]: { text: t("return-to-home"), onAction: handleBack },
		[ButtonAction.CONTACT_SUPPORT]: { text: t("contact-support"), onAction: handleContactSupport },
	} as Record<ButtonAction, { text: string, onAction: () => void } | undefined>;

	const getButtonProps = (action: ButtonAction) => buttonActionMappings[action] || null;

	const errorProps = getErrorProps();
	const buttonProps = getButtonProps(errorProps.buttonAction);

	return <ServiceUnavailablePage
		statusCode={errorProps.statusCode}
		header={errorProps.header}
		description={errorProps.description}
		buttonText={buttonProps?.text}
		buttonAction={buttonProps?.onAction}
	/>
}

export default SpecificErrorPage;
